/**
 * widget-ui — wspólne elementy kafelków pulpitu wg Figmy „08.6 · Cards & widgets":
 * formatowanie wartości (kwota / godziny / liczba), chip trendu (▲ +12,4 %), kafelek z ikoną,
 * czas względny („2 godz. temu") i segmentowy przełącznik okresu (7D / 30D / 12M).
 *
 * Czysta prezentacja bez własnego pobierania danych — kafelki przekazują gotowe wartości.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';

import { Button } from '@/components/Button';
import { resolveIcon } from '@/lib/icon-map';
import { cn } from '@/lib/utils';

import type { DashboardPeriod, KpiUnit } from './types';

// --- formatowanie ---------------------------------------------------------------------------

const NUMBER = new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 0 });
const DECIMAL = new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 1 });
const MONEY = new Intl.NumberFormat('pl-PL', {
  style: 'currency',
  currency: 'PLN',
  maximumFractionDigits: 0,
});

/** Wartość KPI w jej jednostce: `128 400 zł`, `1,8 dnia` / `5,2 godz.`, `248`. */
export function formatKpiValue(
  value: number,
  unit: KpiUnit = 'count',
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (unit === 'amount') return MONEY.format(value);
  if (unit === 'hours') {
    if (value >= 24) {
      return t('tenant.dashboard.units.days', { value: DECIMAL.format(value / 24) });
    }
    return t('tenant.dashboard.units.hours', { value: DECIMAL.format(value) });
  }
  return NUMBER.format(value);
}

/** Kwota w podanej walucie (domyślnie PLN), z zaokrągleniem do groszy tylko gdy trzeba. */
export function formatMoney(value: number, currency = 'PLN'): string {
  const hasCents = Math.abs(value % 1) > 0.004;
  return new Intl.NumberFormat('pl-PL', {
    style: 'currency',
    currency,
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(value);
}

/** Zmiana procentowa `current` względem `previous`; `null` gdy nie da się policzyć. */
export function deltaPercent(current: number, previous: number | null | undefined): number | null {
  if (previous === null || previous === undefined) return null;
  if (previous === 0) return current === 0 ? 0 : null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

/** `+12,4 %` / `−3,1 %` (znak minus typograficzny, jak w Figmie). */
export function formatDelta(pct: number): string {
  const abs = DECIMAL.format(Math.abs(pct));
  if (pct > 0) return `+${abs} %`;
  if (pct < 0) return `−${abs} %`;
  return `${abs} %`;
}

const RTF = new Intl.RelativeTimeFormat('pl', { numeric: 'auto' });

/** Czas względny po polsku („2 godz. temu", „wczoraj"); powyżej tygodnia — krótka data. */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const sec = Math.round((d.getTime() - now.getTime()) / 1000);
  const abs = Math.abs(sec);
  if (abs < 60) return RTF.format(Math.round(sec), 'second');
  if (abs < 3600) return RTF.format(Math.round(sec / 60), 'minute');
  if (abs < 86_400) return RTF.format(Math.round(sec / 3600), 'hour');
  if (abs < 7 * 86_400) return RTF.format(Math.round(sec / 86_400), 'day');
  return d.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// --- chip trendu ----------------------------------------------------------------------------

interface DeltaChipProps {
  /** Zmiana w procentach (z `deltaPercent`). */
  pct: number;
  /** `true` gdy spadek jest dobry (np. czas akceptacji) — odwraca kolory. */
  lowerIsBetter?: boolean;
  className?: string;
}

/** ▲ +12,4 % (zielony) / ▼ −3,1 % (czerwony); zero = neutralny. */
export function DeltaChip({ pct, lowerIsBetter = false, className }: DeltaChipProps) {
  const up = pct > 0;
  const down = pct < 0;
  const good = lowerIsBetter ? down : up;
  const bad = lowerIsBetter ? up : down;
  const Icon = down ? ArrowDownRight : ArrowUpRight;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-medium tabular-nums',
        good && 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        bad && 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
        !good && !bad && 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
        className,
      )}
    >
      {(up || down) && <Icon className="h-3 w-3" aria-hidden="true" />}
      {formatDelta(pct)}
    </span>
  );
}

// --- kafelek z ikoną ------------------------------------------------------------------------

export type Tone = 'accent' | 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'purple';

const TONE_TILE: Record<Tone, string> = {
  accent: 'bg-accent-100 text-accent-700 dark:bg-accent-900/30 dark:text-accent-300',
  success: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  danger: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  info: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  neutral: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
};

interface IconTileProps {
  /** Nazwa ikony z `icon-map`. */
  icon: string;
  tone?: Tone;
  /** Rozmiar kafelka: `sm` 28 px, `md` 40 px. */
  size?: 'sm' | 'md';
  className?: string;
}

export function IconTile({ icon, tone = 'accent', size = 'md', className }: IconTileProps) {
  const IconCmp = resolveIcon(icon);
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-lg',
        size === 'sm' ? 'h-7 w-7' : 'h-10 w-10',
        TONE_TILE[tone],
        className,
      )}
    >
      <IconCmp className={size === 'sm' ? 'h-4 w-4' : 'h-5 w-5'} />
    </span>
  );
}

/** Mała kropka statusu (np. „Aktualizacja dziś, 09:12"). */
export function Dot({ tone = 'accent' }: { tone?: Tone }) {
  const color: Record<Tone, string> = {
    accent: 'bg-accent-500',
    success: 'bg-green-500',
    warning: 'bg-amber-500',
    danger: 'bg-red-500',
    info: 'bg-blue-500',
    neutral: 'bg-gray-400',
    purple: 'bg-purple-500',
  };
  return (
    <span
      aria-hidden="true"
      className={cn('inline-block h-1.5 w-1.5 shrink-0 rounded-full', color[tone])}
    />
  );
}

// --- przełącznik okresu (segmentowy) --------------------------------------------------------

/** Okresy w przełączniku segmentowym wykresów (kalendarzowe okna backendu). */
export const SEGMENT_PERIODS: DashboardPeriod[] = ['week', 'month', 'year'];

interface PeriodSegmentedProps {
  value: DashboardPeriod;
  onChange: (period: DashboardPeriod) => void;
}

/** Segmentowy przełącznik „Tydzień | Miesiąc | Rok" (Figma: Segmented control 7D/30D/12M). */
export function PeriodSegmented({ value, onChange }: PeriodSegmentedProps) {
  const { t } = useTranslation();
  return (
    // stopPropagation — nagłówek kafelka bywa uchwytem przeciągania w trybie edycji.
    <div
      role="group"
      aria-label={t('tenant.dashboard.period.label')}
      onMouseDown={(e) => e.stopPropagation()}
      className="inline-flex rounded-lg bg-bg-tertiary p-0.5"
    >
      {SEGMENT_PERIODS.map((p) => {
        const active = p === value;
        return (
          <Button
            key={p}
            variant="ghost"
            size="sm"
            aria-pressed={active}
            onClick={() => onChange(p)}
            className={cn(
              'h-6 rounded-md px-2 text-[11px] font-medium leading-4',
              active
                ? 'bg-bg-elevated text-text-primary shadow-sm hover:bg-bg-elevated'
                : 'text-text-tertiary hover:bg-transparent hover:text-text-primary',
            )}
          >
            {t(`tenant.dashboard.period.segment.${p}`)}
          </Button>
        );
      })}
    </div>
  );
}

/** Pasek postępu (np. wydajność procesu) w kolorze progu. */
export function ProgressBar({ pct, color }: { pct: number; color: string }) {
  const width = Math.max(0, Math.min(100, pct));
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-bg-tertiary">
      <div
        className="h-full rounded-full transition-[width]"
        style={{ width: `${width}%`, background: color }}
      />
    </div>
  );
}

/** Dwukolumnowy układ „etykieta po lewej, wartość po prawej" używany w listach kafelków. */
export function Row({ left, right }: { left: ReactNode; right: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="min-w-0 flex-1">{left}</div>
      <div className="shrink-0 text-right">{right}</div>
    </div>
  );
}
